import React, { useEffect, useState } from 'react';
import {
  Col,
  Input,
  InputNumber,
  Modal,
  Row,
  Spin,
  Typography,
  Upload,
} from 'antd';
import { DeleteOutlined, LoadingOutlined } from '@ant-design/icons';
import { get } from 'lodash';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';

dayjs.extend(customParseFormat);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
import { useLocation } from 'react-router-dom';

import ActivityBar from 'components/activityBar';
import PdButton from 'components/pd-button';

import ReturnService from 'services/return';
import UploadService from 'services/upload';
import { errHandling, priceFormat } from 'utils';

const { Text } = Typography;
const { TextArea } = Input;

export function ModalContent({
  actionHandler,
  addressFields,
  courierFields,
  fields,
  isShipment = true,
  liveTracking,
  loadingModal,
  modalType,
  modalValue,
  rentDue,
  setModalValue,
  settingModal,
  tracking,
  trackingNumber,
  userFields,
  value = {},
  selectedPackage = -1,
}) {
  const location = useLocation();
  const [imageList, setImageList] = useState([]);
  const [loadingUpload, setLoadingUpload] = useState(false);
  const [modalImages, setModalImages] = useState({});
  const [returnActivity, setReturnActivity] = useState([]);
  const [totalCharge, setTotalCharge] = useState(
    get(value, fields.NET_PRICE, 0)
  );
  const [uploadURL, setUploadURL] = useState('');

  const urlParams = new URLSearchParams(location.search);
  const id = urlParams.get('id');

  const handleParser = (value) => {
    return value.replace(/[^0-9]\s?|(\.*)/g, '').replace(/^0+/, '');
  };

  const handleFormatter = (value) => {
    return `IDR ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  const objPackage = get(value, 'packages', []).find(
    (pack) => pack.id === selectedPackage
  );

  const enterUpdateTrackingContent = () => {
    return (
      <Row className="tracking-content">
        <Col span={24}>
          <Col span={6}>
            <Text strong>Invoice Number</Text>
          </Col>
          <Col span={18}>
            <Text type="secondary">
              {get(objPackage, fields.INVOICE_NUMBER, '')}
            </Text>
          </Col>

          <Col span={6}>
            <Text strong>Customer Name</Text>
          </Col>
          <Col span={18}>
            <Text type="secondary">
              {get(get(value, fields.ADDRESS, {}), addressFields.RECIPIENT, '')}
            </Text>
          </Col>

          <Col span={6}>
            <Text strong>Courier</Text>
          </Col>
          <Col span={18}>
            <Text type="secondary">
              {get(
                get(objPackage, fields.COURIER, {}),
                courierFields.TITLE,
                ''
              )}
            </Text>
          </Col>

          <Col span={6}>
            <Text strong>Shipping Method</Text>
          </Col>
          <Col span={18}>
            <Text type="secondary">
              {get(objPackage, fields.SHIPPING_METHOD, '')}
            </Text>
          </Col>

          <Col span={6}>
            <Text strong>Shipping Cost</Text>
          </Col>
          <Col span={18}>
            <Text type="secondary">
              IDR {priceFormat(get(objPackage, fields.SHIPPING_COST, '0'))}
            </Text>
          </Col>

          <Col span={6}>
            <Text strong>Tracking Code</Text>
          </Col>
          <Col span={16}>
            <Input
              defaultValue={trackingNumber}
              onChange={changeValue.bind(this, 'tracking_code')}
              placeholder="Enter tracking code here"
            />
          </Col>
        </Col>

        <Col span={24}>
          <div className="buttonSection buttonSection-row mt-2">
            <PdButton
              className="button mr-10"
              onClick={
                trackingNumber
                  ? actionHandler.bind(this, 'update_tracking')
                  : actionHandler.bind(this, 'enter_tracking')
              }
              type="primary">
              Save
            </PdButton>
            <PdButton
              className="button"
              onClick={actionHandler.bind(this, 'cancel')}
              type="danger">
              Cancel
            </PdButton>
          </div>
        </Col>
      </Row>
    );
  };

  const rejectContent = () => {
    return (
      <Row className="squeeze-content">
        <Col span={24}>
          <Text>
            As the order has been paid, you have to refund the fund to the
            customer.
          </Text>
        </Col>

        <Col className="mt-2" span={24}>
          <Text>Rejection Reason</Text>
        </Col>

        <Col span={24}>
          <TextArea
            rows={4}
            placeholder="Enter reasons here"
            onChange={changeValue.bind(this, 'reason')}
          />
        </Col>

        <Col span={24}>
          <div className="buttonSection buttonSection-row mt-2">
            <PdButton
              className="button mr-10"
              disabled={get(modalValue, 'reason', '') ? false : true}
              onClick={actionHandler.bind(this, 'reject_order')}
              type="primary">
              Save
            </PdButton>
            <PdButton
              className="button"
              onClick={actionHandler.bind(this, 'cancel')}
              type="danger">
              Cancel
            </PdButton>
          </div>
        </Col>
      </Row>
    );
  };

  const rejectReturnContent = () => {
    return (
      <Row className="squeeze-content">
        <Col className="mt-2" span={24}>
          <Text>Rejection Reason</Text>
        </Col>

        <Col span={24}>
          <TextArea
            onChange={changeValue.bind(this, 'reason')}
            placeholder="Enter reasons here"
            rows={4}
          />
        </Col>

        <Col span={24}>
          <div className="buttonSection buttonSection-row mt-2">
            <PdButton
              className="button mr-10"
              disabled={get(modalValue, 'reason', '') ? false : true}
              onClick={actionHandler.bind(this, 'reject_return')}
              type="primary">
              Save
            </PdButton>
            <PdButton
              className="button"
              onClick={actionHandler.bind(this, 'cancel')}
              type="danger">
              Cancel
            </PdButton>
          </div>
        </Col>
      </Row>
    );
  };

  const trackingContent = () => {
    return (
      <Row>
        {get(value, fields.COURIER, {}).code === 'gosend' && (
          <Row className="gosend-tracking-content">
            <Col span={24}>
              <PdButton onClick={openLiveTracking} outline type="primary">
                Live Tracking
              </PdButton>
            </Col>
          </Row>
        )}
        <div className="modalContent modalContent-center">
          {loadingModal ? (
            <Spin indicator={<LoadingOutlined />} size="large" />
          ) : (
            <div className="modalContent-100">
              {tracking.map((track, index) => (
                <Row key={index}>
                  <Col span={4}>{formatDate(track.manifest_date)}</Col>
                  <Col span={4}>{track.manifest_time}</Col>
                  <Col span={7}>{track.manifest_description}</Col>
                  <Col span={9}>{track.city_name}</Col>
                </Row>
              ))}
            </div>
          )}
        </div>
      </Row>
    );
  };

  const chargeContent = () => {
    return (
      <Row className="modal-content-charge">
        <Col className="detail">
          <Col sm={{ offset: 4, span: 10 }}>Rent Due</Col>
          <Col span={10}>
            <Text strong>{rentDue}</Text>
          </Col>
          <Col sm={{ offset: 4, span: 10 }}>Order Number</Col>
          <Col span={10}>
            <Text strong>{get(value, fields.ID, '')}</Text>
          </Col>
          <Col sm={{ offset: 4, span: 10 }}>Customer Name</Col>
          <Col span={10}>
            <Text strong>
              {get(get(value, fields.USER, {}), userFields.NAME, '')}
            </Text>
          </Col>
        </Col>
        <Col className="mt-2" span={24}>
          <Text strong>Confirm amount to be charged</Text>
        </Col>
        <Col className="mt-2" span={24}>
          <Col span={8}>
            <Text>Rental Price</Text>
          </Col>
          <Col className="disable-input" sm={{ offset: 1, span: 15 }}>
            <InputNumber
              defaultValue={get(value, fields.NET_PRICE, 0)}
              disabled={true}
              formatter={handleFormatter}
            />
          </Col>
          <Col span={8}>
            <Text>Penalty Amount</Text>
          </Col>
          <Col sm={{ offset: 1, span: 15 }}>
            <InputNumber
              defaultValue={0}
              formatter={handleFormatter}
              onChange={changeValue.bind(this, 'charge_amount')}
              parser={handleParser}
            />
          </Col>
          <Col span={8}>
            <Text>Total Charge</Text>
          </Col>
          <Col className="disable-input" sm={{ offset: 1, span: 15 }}>
            <InputNumber
              disabled={true}
              formatter={handleFormatter}
              value={totalCharge}
            />
          </Col>
        </Col>
        <Col className="mt-2" span={24}>
          <TextArea
            onChange={changeValue.bind(this, 'reason')}
            placeholder="Enter penalty explanation"
            rows={4}
          />
        </Col>
        {imageList.length !== 0 &&
          imageList.map((image, idx) => (
            <Col
              className="mt-2"
              key={idx}
              sm={{ offset: idx > 0 ? 1 : 0, span: 8 }}
              xs={5}>
              <div
                className="img img--medium no-margin"
                style={{
                  backgroundImage: `url(${image}), url(/img/no-image.png)`,
                }}
              />
              <div className="delete-img" onClick={deleteImage.bind(this, idx)}>
                <DeleteOutlined />
              </div>
            </Col>
          ))}
        {imageList.length < 3 && (
          <Col
            className="mt-2"
            sm={{ offset: imageList.length !== 0 ? 1 : 0, span: 8 }}>
            <Upload
              beforeUpload={beforeUpload}
              className="w-full"
              customRequest={(file) => awsUpload(file, 'profile')}
              onChange={handleChangePhoto}
              showUploadList={false}>
              <PdButton
                outline={true}
                type="primary"
                full={true}
                loading={loadingUpload}>
                Add Photo
              </PdButton>
            </Upload>
          </Col>
        )}
        <Col span={24}>
          <div className="buttonSection buttonSection-row mt-2">
            <PdButton
              className="button mr-10"
              disabled={get(modalValue, 'reason', '') ? false : true}
              onClick={actionHandler.bind(this, 'charged')}
              type="primary">
              Charge
            </PdButton>
          </div>
        </Col>
      </Row>
    );
  };

  const returnContent = () => {
    return (
      <>
        <div className="modal-body">
          {returnActivity.map((track, idx) => (
            <>
              <Row>
                {idx !== 0 && (
                  <Col
                    className="top-divider"
                    sm={{ offset: 7, span: 17 }}></Col>
                )}
              </Row>
              <div className="activity-row" key={track.id}>
                <ActivityBar
                  actor={track.actor.full_name}
                  dataType={track.verb}
                  date={track.created_at}
                  desc={track.message}
                  msgData={track.message_data}
                />

                {track.verb === 'create_return' && (
                  <>
                    {track.images.length > 0 && (
                      <>
                        <Row>
                          <Col className="mt-2 py-12" offset={7}>
                            Images:
                          </Col>
                        </Row>
                        <Row className="image-bar">
                          {track.images.map((path, idx) => (
                            <Col
                              className="py-12"
                              key={idx}
                              lg={4}
                              offset={idx === 0 ? 7 : 0}>
                              <img
                                alt="img"
                                onClick={handleImageModal.bind(this, path)}
                                src={path}
                              />
                            </Col>
                          ))}
                        </Row>
                      </>
                    )}
                    {returnActivity[0].verb === 'create_return' && (
                      <Row>
                        <Col className="button-group" lg={12} offset={7}>
                          <PdButton
                            className="button-action"
                            onClick={actionHandler.bind(this, 'accept_return')}
                            type="primary">
                            Accept
                          </PdButton>
                          <PdButton
                            className="button-action"
                            onClick={settingModal.bind(this, 'REJECT_RETURN')}
                            outline
                            type="danger">
                            Reject
                          </PdButton>
                        </Col>
                      </Row>
                    )}
                  </>
                )}
                {track.verb === 'confirm_shipment' &&
                  returnActivity[0].verb === 'confirm_shipment' && (
                    <Row justify="center">
                      <Col className="button-group" lg={12} offset={7}>
                        {get(value, 'type', 'buy') === 'buy' && (
                          <>
                            <PdButton
                              className="button-action"
                              onClick={actionHandler.bind(
                                this,
                                'accept_shipment'
                              )}
                              type="primary">
                              Confirm
                            </PdButton>
                            <PdButton
                              className="button-danger"
                              onClick={settingModal.bind(this, 'REJECT_RETURN')}
                              type="danger">
                              Reject
                            </PdButton>
                          </>
                        )}
                        {get(value, 'type', 'buy') === 'rent' && (
                          <>
                            <PdButton
                              className="button-action"
                              onClick={actionHandler.bind(
                                this,
                                'accept_shipment'
                              )}
                              type="primary">
                              Confirm
                            </PdButton>
                          </>
                        )}
                      </Col>
                    </Row>
                  )}
              </div>
            </>
          ))}
        </div>

        <Modal
          className="modal-image"
          footer={null}
          onCancel={cancelModalImages}
          open={modalImages.visible}
          width={800}>
          <Row>
            <Col>
              <img alt="img" src={modalImages.image} />
            </Col>
          </Row>
        </Modal>
      </>
    );
  };

  const modalTypeCollections = {
    charge: chargeContent(),
    enter_tracking: enterUpdateTrackingContent(),
    reject_order: rejectContent(),
    reject_return: rejectReturnContent(),
    return: returnContent(),
    tracking_order: trackingContent(),
    update_tracking: enterUpdateTrackingContent(),
  };

  async function awsUpload(file) {
    try {
      setLoadingUpload(true);

      await UploadService.uploadImage(file.file, uploadURL.upload_url);

      setImageList((prevState) => [...prevState, uploadURL.secure_url]);
    } catch (error) {
      errHandling(error);
    } finally {
      setLoadingUpload(false);
    }
  }

  async function beforeUpload(file) {
    try {
      const ext = file.type.split('/')[1];
      const { data } = await UploadService.getUploadURL(ext, 'profile');
      const urls = data.data;

      setUploadURL(urls);
    } catch (error) {
      errHandling(error);
    }
  }

  async function getOrderActivity() {
    try {
      if (id && location.pathname !== '/orders/detail') {
        const returnActivity = await ReturnService.getOrderActivity(id);
        setReturnActivity(returnActivity.data.data.activities);
      }
    } catch (error) {
      errHandling(error);
    }
  }

  function handleImageModal(path) {
    setModalImages({
      visible: true,
      image: path,
    });
  }

  function cancelModalImages() {
    setModalImages({
      visible: false,
      images: '',
    });
  }

  function getBase64(img, callback) {
    const reader = new FileReader();

    reader.addEventListener('load', () => callback(reader.result));
    reader.readAsDataURL(img);
  }

  function handleChangePhoto(info) {
    if (info.file.status === 'uploading') {
      setLoadingUpload(true);
    }

    if (info.file.status === 'done') {
      getBase64(info.file.originFileObj, (imageUrl) => {
        setImageList((prevState) => [...prevState, imageUrl]);
        setLoadingUpload(false);
      });
    }
  }

  function deleteImage(index) {
    const currentList = imageList.slice();
    currentList.splice(index, 1);
    setImageList(currentList);
  }

  function formatDate(str) {
    return dayjs(str).format('DD/MM/YYYY');
  }

  function openLiveTracking() {
    return window.open(liveTracking, '_blank');
  }

  function changeValue(key, event) {
    let val = get(event, 'target.value', event);
    let newModalValue = { ...modalValue };
    let total;
    switch (key) {
      case 'tracking_code':
        newModalValue = { ...newModalValue, tracking_code: val.trim() };
        break;
      case 'reason':
        newModalValue = { ...newModalValue, reason: val };
        break;
      case 'charge_amount':
        total = +val + +get(value, fields.NET_PRICE, 0);
        if (+val < 1) newModalValue = { ...newModalValue, charge_amount: 0 };
        else newModalValue = { ...newModalValue, charge_amount: total };
        newModalValue = { ...newModalValue, image_list: imageList };
        setTotalCharge(total);
        break;
      default:
        break;
    }
    setModalValue(newModalValue);
  }

  useEffect(() => {
    if (!isShipment) getOrderActivity();
    // eslint-disable-next-line
  }, []);

  return <div className="modal-content">{modalTypeCollections[modalType]}</div>;
}

export default { ModalContent };
